import { useLoginMutation, useLazyGetUserDetailsQuery } from 'util/rtk-api'
import { useDocTitle } from 'util/hooks'

import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import 'css/pages/login.css'
import { AuthBackdrop } from 'auth/auth-backdrop'

export const Login = () => {
	const navigate = useNavigate()
	const [ login ] = useLoginMutation()
	const [ triggerGetUserDetails ] = useLazyGetUserDetailsQuery()

	const [ username, setUsername ] = useState()
	const [ password, setPassword ] = useState()
	const [ error, setError ] = useState(null)

	// Redirect to dashboard if already logged in
	useEffect(() => {
		const loggedIn = localStorage.getItem('loggedIn')
		const expiration = new Date(30 * 24 * 60 * 60 * 1000).getTime()
		if (loggedIn && Date.now() - loggedIn < expiration) {
			const isStudent = localStorage.getItem('studentId')
			navigate(isStudent ? '/enter-code' : '/dashboard', { replace: true })
		}
	}, [navigate])

	const onUsernameBlur = ({ target }) => {
		setUsername(target.value)
	}

	const onPasswordBlur = ({ target }) => {
		setPassword(target.value)
	}

	const onFormSubmit = async event => {
		event.preventDefault()

		const result = await login({ username, password })

		if ('error' in result) {
			const err = result.error

			// err-12: a network failure used to reject unhandled and do NOTHING
			if (err.status === 'FETCH_ERROR' || err.status === 'TIMEOUT_ERROR') {
				setError("We couldn't reach the server. Check your internet connection and try again.")
				return
			}

			// Handle Cognito challenges
			if (err.status === 401) {
				const body = err.data
				if (body?.challenge === 'NEW_PASSWORD_REQUIRED' && body?.session) {
					navigate(`/new-password-required?username=${encodeURIComponent(username)}&session=${encodeURIComponent(body.session)}`)
					return
				}
			}

			// err-12: the login throttle (429) and server errors used to show
			// "Incorrect email or password", inviting more attempts
			if (err.status === 429) {
				setError('Too many login attempts. Please wait a few minutes and try again.')
				return
			}
			if (typeof err.status === 'number' && err.status >= 500) {
				setError('Something went wrong on our side. Please try again in a moment.')
				return
			}

			setError("Incorrect email or password. Please try again.")
			return
		}

		localStorage.setItem('loggedIn', Date.now())
		// After login, check account type to route students to enter code
		try {
			const details = await triggerGetUserDetails().unwrap()
			if (details?.accountType === 'student') {
				// Persist student info for later use in student flows
				if (details?.userId) localStorage.setItem('studentId', details.userId)
				if (details?.firstName) localStorage.setItem('firstName', details.firstName)
				navigate('/enter-code')
				return
			}
			// Ensure studentId is cleared for non-student accounts
			localStorage.removeItem('studentId')
		} catch (e) {
			// fallback to dashboard on failure
			localStorage.removeItem('studentId')
		}
		navigate('/dashboard')
	}

	useDocTitle('All for One - Log in')

	return (
		<div className="auth-page">
			<AuthBackdrop />
		<section className="login-container">
			{/* mockup .login-card head: eyebrow, title, rule, sub-copy */}
			<span className="login-eyebrow">Welcome back</span>
			<h1 className="login-header">Hey <span className="highlight">there!</span></h1>
			<div className="login-rule" />
			<p className="login-sub">Log in to keep designing lessons for every mind.</p>

			<form
				id="login-form"
				action="#"
				method="POST"
				onSubmit={onFormSubmit}
			>
				<div className="form-group">
					{/* the mockup labels both fields above the input rather than
					    relying on the placeholder alone */}
					<label htmlFor="username" className="field-label">Username or email</label>
					<input
						type="text"
						id="username"
						name="username"
						placeholder="Username or email"
						required
						onChange={onUsernameBlur}
					/>
				</div>

				<div className="form-group">
					<label htmlFor="password" className="field-label">Password</label>
					<input
						type="password"
						id="password"
						name="password"
						placeholder="Password"
						required
						onChange={onPasswordBlur}
					/>
					<Link to="/forgot-password" className="forgot-password">Forgot password?</Link>
				</div>

				{error && <p className="login-error">{error}</p>}

				<div className="form-group">
					<button type="submit" className="login-btn">Log in</button>
				</div>
			</form>

			<div className="auth-divider"><span>or</span></div>

			<p className="signup-prompt">
				Don't have an account? <Link to="/signup" className="signup-link">Sign up!</Link>
			</p>
		</section>
		</div>
	)
}
